import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkUtilsService, URL_DICT } from 'projects/app-core/src/service/network-utils.service';

// Mirrors anyobackendapi's GoalStatus enum (goalModels.ts) exactly — character-identical values.
export enum GoalStatus {
  ACTIVE = 'active',
  PAUSED = 'paused',
  ABANDONED = 'abandoned',
}

export enum GoalStatusChangeActor {
  USER = 'user',
  ADMIN = 'admin',
}

export interface GoalStatusChangeRecord {
  changedAt: string;
  fromStatus: GoalStatus;
  toStatus: GoalStatus;
  reason?: string;
  actor: GoalStatusChangeActor;
  adminActorId?: string;
}

export interface GoalAudit {
  rationale: string;
  heroReason?: string;
  guardrailFlags?: { ruleId: string; check: string; detail: string }[];
  failures?: unknown[];
}

export interface ConfigChangeRecord {
  changedAt: string;
  changeType: string;
  previousConfig: Record<string, unknown>;
  newConfig: Record<string, unknown>;
  reason: string;
  guardrailFlags: { ruleId: string; check: string; detail: string }[];
  rampReasoning?: string;
  holdReason?: string;
}

export interface PlanProgressorState {
  needsGoalAgentHandback?: boolean;
  terminalOptionsNeeded?: boolean;
  disengagementMenuNeeded?: boolean;
  rebaselineOfferNeeded?: boolean;
  dormancyFrozenAt?: string;
  rampWithoutDestinationFlagged?: boolean;
}

// A day-record whose outcome was human-asserted (expert-portal markDayHitOverride) rather than
// computed from real signal data. Lives on planProgressorDayRecords, not configHistory — a
// different collection/audit trail.
export interface ManualDayOverride {
  date: string;
  markedBy: string;
  overrideReason?: string;
  computedAt: string;
}

export interface AdminGoalObjectiveView {
  userObjectiveId: string;
  objectiveId: string;
  type: string;
  displayTitle?: string;
  displayDescription?: string;
  status: string;
  config: Record<string, unknown>;
  configHistory: ConfigChangeRecord[];
  planProgressorState?: PlanProgressorState;
  manualDayOverrides: ManualDayOverride[];
}

export interface AdminGoalView {
  goalId: string;
  status: GoalStatus;
  heroPillar?: string;
  startDate: string;
  endDate?: string;
  pausedAt?: string;
  abandonReason?: string;
  audit: GoalAudit;
  statusHistory: GoalStatusChangeRecord[];
  assignedExpertId?: string;
  concernId?: string;
}

export interface AdminGoalLifecycleView {
  userId: string;
  goal: AdminGoalView | null;
  objectives: AdminGoalObjectiveView[];
}

// Mirrors anyobackendapi's GoalLifecycleError enum (goalPersistence.ts) exactly.
export enum GoalLifecycleError {
  NO_ACTIVE_GOAL = 'no_active_goal',
  NOT_PAUSED = 'not_paused',
}

export interface GoalLifecycleResult {
  success: boolean;
  goalId?: string;
  status?: 'paused' | 'abandoned' | 'resumed';
  error?: GoalLifecycleError;
}

// Mirrors anyobackendapi's AdminChangeExpertError enum (goalAccountabilityAssignment.ts) exactly.
export enum AdminChangeExpertError {
  NO_ACTIVE_GOAL = 'no_active_goal',
  EXPERT_NOT_ELIGIBLE = 'expert_not_eligible',
}

export interface AdminChangeExpertResult {
  success: boolean;
  goalId?: string;
  assignedExpertId?: string;
  error?: AdminChangeExpertError;
}

// Mirrors anyobackendapi's AdminAssignExpertToReferralError enum (goalAccountabilityAssignment.ts) exactly.
export enum AdminAssignExpertToReferralError {
  REFERRAL_NOT_FOUND = 'referral_not_found',
  REFERRAL_NOT_AWAITING_EXPERT = 'referral_not_awaiting_expert',
  REFERRAL_ALREADY_ASSIGNED = 'referral_already_assigned',
  EXPERT_NOT_ELIGIBLE = 'expert_not_eligible',
  EXPERT_MISSING_GOALS_ROLE = 'expert_missing_goals_role',
  EXPERT_ROLE_UNVERIFIABLE = 'expert_role_unverifiable',
}

export interface AdminAssignExpertToReferralResult {
  success: boolean;
  referralId?: string;
  assignedExpertId?: string;
  /** False when the assignment stands but the member chat could not be created. */
  chatCreated?: boolean;
  error?: AdminAssignExpertToReferralError;
}

export interface PlanProgressorMonitorGridRequest {
  page: number;
  pageSize: number;
  export: boolean;
  sort?: { LAST_MODIFIED_AT?: 'ASC' | 'DESC' };
  filters?: {
    HANDBACK?: string[];
    TERMINAL?: string[];
    DISENGAGE?: string[];
    DORMANT?: string[];
    REBASELINE?: string[];
    RAMP_WITHOUT_DESTINATION?: string[];
    HOLD_REASON?: string[];
    STATUS?: string[];
    /** Absent, or ["true"], excludes test users (the default). Send ["false"] to include them. */
    EXCLUDE_TEST_USERS?: string[];
    USER_TAGS?: string[];
    PROGRESS_BUCKET?: string[];
  };
  searchQuery?: {
    EXPERT_TITLE?: string;
    DISPLAY_TITLE?: string;
    USER_EMAIL?: string;
    PARTNER_ID?: string;
  };
  globalSearch?: string[];
}

export interface PlanProgressorMonitorGridItem {
  userObjectiveId: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  partnerId?: string;
  userTags?: string[];
  objectiveId: string;
  objectiveTitle?: string;
  objectiveIsArchived?: boolean;
  displayTitle?: string;
  displayDescription?: string;
  status: string;
  latestSnapshot?: {
    snapshotId: string;
    computedAt: string;
    progress: number;
    status: string;
    facts?: Record<string, unknown>;
  };
  flags: {
    needsGoalAgentHandback: boolean;
    terminalOptionsNeeded: boolean;
    disengagementMenuNeeded: boolean;
    dormant: boolean;
    rebaselineOfferNeeded: boolean;
    rampWithoutDestinationFlagged: boolean;
  };
  lastHoldReason?: string;
  lastModifiedAt?: string;
}

export interface PlanProgressorMonitorGridResponse {
  filterOptions?: unknown;
  data: PlanProgressorMonitorGridItem[];
  pageNum: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

// Mirrors anyobackendapi's ObjectiveCategory enum (objectiveModels.ts) exactly.
export enum ObjectiveCategory {
  MOVEMENT = 'movement',
  SLEEP = 'sleep',
  NUTRITION = 'nutrition',
  HYDRATION = 'hydration',
  MOOD = 'mood',
  PRACTICE = 'practice',
  ENGAGEMENT = 'engagement',
  CUSTOM = 'custom',
}

// Mirrors anyobackendapi's UserObjectiveStatus enum (objectiveModels.ts) exactly.
export enum UserObjectiveStatus {
  ACTIVE = 'active',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  EXPIRED = 'expired',
}

// Mirrors anyobackendapi's UserTags enum (IAnyoUser.ts) exactly.
export enum UserTags {
  TESTING = 'TESTING',
  SILENT_NOTIFICATION_NOT_DELIVERED = 'SILENT_NOTIFICATION_NOT_DELIVERED',
  SILENT_NOTIFICATION_DELIVERED = 'SILENT_NOTIFICATION_DELIVERED',
  CORPORATE_FAMILY_MEMBER = 'CORPORATE_FAMILY_MEMBER',
  WHATSAPP_DELIVERED = 'WHATSAPP_DELIVERED',
  WHATSAPP_NOT_DELIVERED = 'WHATSAPP_NOT_DELIVERED',
  WHATSAPP_FLOW = 'WHATSAPP_FLOW',
  ONBOARDING_NOT_COMPLETED = 'ONBOARDING_NOT_COMPLETED',
  SESSION_BOOKING_BLOCKED = 'SESSION_BOOKING_BLOCKED',
}

export interface ObjectivesGridRequest {
  page: number;
  pageSize: number;
  export: boolean;
  sort?: { CREATED_AT?: 'ASC' | 'DESC'; TITLE?: 'ASC' | 'DESC' };
  filters?: {
    PILLAR?: string[];
    CATEGORY?: string[];
    IS_ARCHIVED?: string[];
  };
  searchQuery?: { TITLE?: string };
}

export interface ObjectivesGridResponseData {
  objectiveId: string;
  title: string;
  description?: string;
  expertTitle?: string;
  expertDescription?: string;
  category?: ObjectiveCategory;
  pillar: string;
  isArchived: boolean;
  retiredAt?: string;
  retiredBy?: string;
  version: number;
  createdAt?: string;
  activeUserObjectiveCount: number;
}

export interface ObjectivesGridResponse {
  filterOptions?: unknown;
  data: ObjectivesGridResponseData[];
  pageNum: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

// Mirrors anyobackendapi's UserObjectivesProgressBucket enum (adminObjectivesGridModels.ts) exactly.
export enum UserObjectivesProgressBucket {
  NO_DATA = 'NO_DATA',
  RANGE_0_25 = '0-25',
  RANGE_26_50 = '26-50',
  RANGE_51_75 = '51-75',
  RANGE_76_100 = '76-100',
}

export interface UserObjectivesGridRequest {
  page: number;
  pageSize: number;
  export: boolean;
  sort?: { START_DATE?: 'ASC' | 'DESC'; LAST_MODIFIED_AT?: 'ASC' | 'DESC' };
  filters?: {
    STATUS?: string[];
    PILLAR?: string[];
    /** Absent, or ["true"], excludes test users (the default). Send ["false"] to include them. */
    EXCLUDE_TEST_USERS?: string[];
    USER_TAGS?: string[];
    PROGRESS_BUCKET?: string[];
  };
  searchQuery?: {
    /** Internal-only, set by the objectives-catalog "View Users" drill-down link — never a
     * free-text input in this grid's own UI. */
    OBJECTIVE_ID?: string;
    EXPERT_TITLE?: string;
    DISPLAY_TITLE?: string;
    USER_EMAIL?: string;
    PARTNER_ID?: string;
  };
}

export interface UserObjectivesGridResponseData {
  userObjectiveId: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  partnerId?: string;
  userTags?: string[];
  objectiveId: string;
  displayTitle?: string;
  displayDescription?: string;
  status: UserObjectiveStatus;
  pillar: string;
  config: Record<string, unknown>;
  latestSnapshot?: {
    snapshotId: string;
    computedAt: string;
    progress: number;
    status: string;
    facts?: Record<string, unknown>;
  };
  startDate: string;
  lastModifiedAt?: string;
}

export interface UserObjectivesGridResponse {
  filterOptions?: unknown;
  data: UserObjectivesGridResponseData[];
  pageNum: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

// Mirrors anyobackendapi's Pillar enum (heroPillar.ts) exactly.
export enum Pillar {
  ACTIVITY = 'Activity',
  SLEEP = 'Sleep',
  NUTRITION = 'Nutrition',
  ZEN = 'Zen',
  SOCIAL = 'Social',
}

// Mirrors anyobackendapi's GoalSource enum (goalModels.ts) exactly.
export enum GoalSource {
  SELF_GENERATED = 'SELF_GENERATED',
  EXPERT_GENERATED = 'EXPERT_GENERATED',
}

export interface GoalsGridRequest {
  page: number;
  pageSize: number;
  export: boolean;
  sort?: { START_DATE?: 'ASC' | 'DESC'; LAST_MODIFIED_AT?: 'ASC' | 'DESC' };
  filters?: {
    STATUS?: string[];
    HERO_PILLAR?: string[];
    SOURCE?: string[];
    IS_ARCHIVED?: string[];
    /** Absent, or ["true"], excludes test users (the default). Send ["false"] to include them. */
    EXCLUDE_TEST_USERS?: string[];
  };
  searchQuery?: {
    USER?: string;
    PARTNER_ID?: string;
    ASSIGNED_EXPERT_NAME?: string;
  };
  dateFilters?: {
    START_DATE?: { from: string; to?: string };
  };
}

export interface GoalsGridResponseData {
  goalId: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  partnerId?: string;
  userTags?: string[];
  heroPillar?: string;
  source: GoalSource;
  status: GoalStatus;
  startDate: string;
  endDate?: string;
  pausedAt?: string;
  abandonReason?: string;
  assignedExpertId?: string;
  assignedExpertName?: string;
  objectiveCount: number;
  lastModifiedAt?: string;
  isArchived: boolean;
}

export interface GoalsGridResponse {
  filterOptions?: unknown;
  data: GoalsGridResponseData[];
  pageNum: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

/** Only page and pageSize are read by the backend; rows carry no member identity by design. */
export interface UnassignedReferralsGridRequest {
  page: number;
  pageSize: number;
  export: boolean;
}

export interface UnassignedReferralsGridResponseData {
  referralId: string;
  createdAt: string;
  ageHours: number;
  heroPillar?: Pillar;
  reasonText: string;
  partnerId?: string;
}

export interface UnassignedReferralsGridResponse {
  filterOptions?: unknown;
  data: UnassignedReferralsGridResponseData[];
  pageNum: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

export interface AdminForceRecalcResult {
  success: boolean;
  userObjectivesRecalculated?: number;
  recapGenerated?: boolean;
  error?: string;
}

export interface RetireObjectiveResult {
  objectiveId: string;
  expiredUserObjectivesCount: number;
}

export interface RetireUserObjectiveResult {
  userObjectiveId: string;
  alreadyExpired: boolean;
}

// Mirrors anyobackendapi's MarkDayCompleteError enum (planProgressorModels.ts) exactly.
export enum MarkDayCompleteError {
  NOT_FOUND = 'not_found',
  FORBIDDEN = 'forbidden',
  INVALID_DATE = 'invalid_date',
  FUTURE_DATE = 'future_date',
  INVALID_STATE = 'invalid_state',
}

export interface MarkDayCompleteResult {
  success: boolean;
  dayRecordId?: string;
  error?: MarkDayCompleteError;
}

@Injectable({ providedIn: 'root' })
export class GoalsService {
  constructor(private api: NetworkUtilsService) {}

  getGoalLifecycleForUser(userId: string): Observable<AdminGoalLifecycleView> {
    return this.api.get<AdminGoalLifecycleView>(
      URL_DICT.adminGoalsUserLifecycleUrl + userId,
    );
  }

  pauseGoal(userId: string): Observable<GoalLifecycleResult> {
    return this.api.post<GoalLifecycleResult>(
      URL_DICT.adminGoalsPauseUrl(userId),
      {},
    );
  }

  resumeGoal(userId: string): Observable<GoalLifecycleResult> {
    return this.api.post<GoalLifecycleResult>(
      URL_DICT.adminGoalsResumeUrl(userId),
      {},
    );
  }

  abandonGoal(
    userId: string,
    abandonReason?: string,
  ): Observable<GoalLifecycleResult> {
    return this.api.post<GoalLifecycleResult>(
      URL_DICT.adminGoalsAbandonUrl(userId),
      abandonReason ? { abandonReason } : {},
    );
  }

  changeExpert(
    userId: string,
    newExpertId: string,
  ): Observable<AdminChangeExpertResult> {
    return this.api.post<AdminChangeExpertResult>(
      URL_DICT.adminGoalsChangeExpertUrl(userId),
      { newExpertId },
    );
  }

  /** Support override — for when a user reaches out to support directly rather than their
   * assigned expert. `date` is epoch ms (any instant within the intended user-local calendar
   * day); the backend re-derives that day via the user's own timezone. */
  markObjectiveDayComplete(
    userObjectiveId: string,
    date: number,
    reason: string,
  ): Observable<MarkDayCompleteResult> {
    return this.api.post<MarkDayCompleteResult>(
      URL_DICT.adminGoalsMarkDayCompleteUrl(userObjectiveId),
      { date, reason },
    );
  }

  getPlanProgressorMonitorGrid(
    req: PlanProgressorMonitorGridRequest,
  ): Observable<PlanProgressorMonitorGridResponse> {
    return this.api.post<PlanProgressorMonitorGridResponse>(
      URL_DICT.adminGoalsPlanProgressorMonitorGridUrl,
      req,
    );
  }

  getObjectivesGrid(req: ObjectivesGridRequest): Observable<ObjectivesGridResponse> {
    return this.api.post<ObjectivesGridResponse>(
      URL_DICT.adminGoalsObjectivesGridUrl,
      req,
    );
  }

  getUserObjectivesGrid(
    req: UserObjectivesGridRequest,
  ): Observable<UserObjectivesGridResponse> {
    return this.api.post<UserObjectivesGridResponse>(
      URL_DICT.adminGoalsUserObjectivesGridUrl,
      req,
    );
  }

  retireObjective(objectiveId: string): Observable<RetireObjectiveResult> {
    return this.api.post<RetireObjectiveResult>(
      URL_DICT.adminGoalsRetireObjectiveUrl(objectiveId),
      {},
    );
  }

  retireUserObjective(
    userObjectiveId: string,
  ): Observable<RetireUserObjectiveResult> {
    return this.api.post<RetireUserObjectiveResult>(
      URL_DICT.adminGoalsRetireUserObjectiveUrl(userObjectiveId),
      {},
    );
  }

  getGoalsGrid(req: GoalsGridRequest): Observable<GoalsGridResponse> {
    return this.api.post<GoalsGridResponse>(URL_DICT.adminGoalsGridUrl, req);
  }

  getUnassignedReferralsGrid(
    req: UnassignedReferralsGridRequest,
  ): Observable<UnassignedReferralsGridResponse> {
    return this.api.post<UnassignedReferralsGridResponse>(
      URL_DICT.adminGoalsUnassignedReferralsGridUrl,
      req,
    );
  }

  assignExpertToReferral(
    referralId: string,
    newExpertId: string,
  ): Observable<AdminAssignExpertToReferralResult> {
    return this.api.post<AdminAssignExpertToReferralResult>(
      URL_DICT.adminGoalsAssignExpertToReferralUrl(referralId),
      { newExpertId },
    );
  }

  forceRecalc(userId: string): Observable<AdminForceRecalcResult> {
    return this.api.post<AdminForceRecalcResult>(
      URL_DICT.adminGoalsForceRecalcUrl(userId),
      {},
    );
  }
}
